import { Col, Row } from '@opendoor/atoms/box';
import { Checkbox } from '@opendoor/atoms/checkbox';
import { Input } from '@opendoor/atoms/input';
import { Link } from '@opendoor/atoms/link';
import { Radio } from '@opendoor/atoms/radio';
import { Text } from '@opendoor/atoms/text';
import { IconCoin, IconInfoCircle } from '@tabler/icons-react';
import { DatePicker } from 'components/buyer/buyDirectCheckout/shared/DatePicker';
import { isLoanFinancing } from 'components/offerPortal/offerSubmission';
import {
  isCloseDateValid,
  isMoneyAskValid,
  type OfferPortalWizardContext,
} from 'state_machines/OfferPortalWizardMachine';

import { getContingencyPeriods } from './contingencyPeriods';
import {
  isAllowedCloseDate,
  isFhaEligible,
  type ListingDetails,
  minCloseDaysFromToday,
} from './listingDetails';
import { useFieldErrors } from './useFieldErrors';
import {
  CARD_TITLE,
  CardHeading,
  CONTINGENT_STATUS_OPTIONS,
  FIELD_CARD,
  FIELD_INPUT,
  FIELD_LABEL,
  FieldError,
  FINANCING_OPTIONS,
  LENDER_OPTIONS,
  RADIO_GROUP_AS_CARD_TITLE,
  RADIO_GROUP_AS_FIELD,
  RADIO_ROW,
  type Send,
} from './wizardFields';

const daysFormatter = new Intl.NumberFormat(undefined, {
  style: 'unit',
  unit: 'day',
  unitDisplay: 'long',
  maximumFractionDigits: 0,
});

/** Step 2 of 6 of the offer wizard — financing, close date, and contingency. */
const DetailsStep = ({
  listing,
  context,
  send,
}: {
  listing: ListingDetails;
  context: OfferPortalWizardContext;
  send: Send;
}) => {
  const { shouldShowError, onBlur, markBlurred } = useFieldErrors(context.showError);
  const handleDownPaymentChange = (value: number | null) => {
    send({ type: 'SET_DOWN_PAYMENT_CENTS', value: value != null ? Math.round(value * 100) : null });
  };

  const wholeDays = (next: number | null): number | null =>
    next != null ? Math.round(next) : null;

  const today = new Date();
  const fhaEligible = isFhaEligible(listing, today);
  const minCloseDays = minCloseDaysFromToday(listing, today);
  // Legacy disabled the FHA option outright during the waiting period; the
  // design keeps it selectable with the notice, so the restriction moves to
  // the close date — an FHA offer cannot close before the home is eligible.
  const fhaMinDate =
    context.financingType === 'fha' && !fhaEligible ? (listing.fhaEligibleDate ?? null) : null;
  const closeDateOk = isCloseDateValid(context);
  const closeDateViolation = context.fieldViolations['closeOfEscrow'];
  const closeDateInvalid =
    (shouldShowError('close-date') && !closeDateOk) || closeDateViolation != null;
  const downPaymentMissing = shouldShowError('down-payment') && context.downPaymentCents == null;
  const downPaymentInvalid =
    downPaymentMissing ||
    !isMoneyAskValid(context.downPaymentCents, context.priceCents) ||
    context.fieldViolations['downPaymentCents'] != null;
  const statusOk = !context.contingentOnSale || context.contingentHomeStatus != null;
  const fhaUntil =
    listing.fhaEligibleDate == null
      ? ''
      : new Intl.DateTimeFormat('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        }).format(new Date(`${listing.fhaEligibleDate}T00:00:00`));
  const closeDateError = () => {
    if (!shouldShowError('close-date') || closeDateOk) {
      return undefined;
    }
    if (context.closeDate == null) return 'Select the closing date.';
    if (fhaMinDate != null && context.closeDate != null && context.closeDate < fhaMinDate) {
      return `FHA offers on this home can’t close before ${fhaUntil}. Pick a later date or change the financing type.`;
    }
    if (minCloseDays != null && minCloseDays > 0) {
      return `Closing date cannot be shorter than ${minCloseDays} days from today.`;
    }
    return 'Select the closing date.';
  };
  // Empty string, never undefined: Base UI fixes controlled-ness on the first
  // render, so a group that starts undefined and later holds an answer flips
  // from uncontrolled to controlled and throws.
  const contingentValue = () => {
    if (context.contingentOnSale == null) return '';
    return context.contingentOnSale ? 'yes' : 'no';
  };
  // Only the closing days the home supports are pickable — and none before
  // the eligibility date while FHA is the financing type.
  const isDayOutOfRange = (day: Date): boolean =>
    !isAllowedCloseDate(listing, day, new Date()) ||
    (fhaMinDate != null && day < new Date(`${fhaMinDate}T00:00:00`));
  const periods = getContingencyPeriods(context).map((period) => {
    const { value, waived } = period;
    const errors = [...period.errors];
    if (shouldShowError(period.id) && period.validationError != null) {
      errors.push(period.validationError);
    }
    const unitClassName = waived ? 'text-od-content-inactive-primary' : 'text-od-content-tertiary';
    const dayParts = value == null ? [] : daysFormatter.formatToParts(value);
    return {
      ...period,
      errors,
      invalid: errors.length > 0,
      dayParts: dayParts.map((part) => ({
        ...part,
        className: part.type === 'unit' ? unitClassName : 'invisible',
      })),
    };
  });

  return (
    <>
      <Col className={FIELD_CARD}>
        <Radio.Group
          className={RADIO_GROUP_AS_CARD_TITLE}
          label={
            <Text as="span" className={CARD_TITLE}>
              Financing
            </Text>
          }
          value={context.financingType ?? ''}
          onBlur={onBlur('financing-type')}
          onValueChange={(value) => send({ type: 'SET_FINANCING_TYPE', value: String(value) })}
          error={
            shouldShowError('financing-type') && !context.financingType
              ? 'Select how your client is paying.'
              : undefined
          }
        >
          {FINANCING_OPTIONS.map((option) => (
            <Radio.Item
              key={option.value}
              label={option.label}
              value={option.value}
              className={RADIO_ROW}
            />
          ))}
        </Radio.Group>
        {context.fieldViolations['financingType'] != null ? (
          <FieldError>{context.fieldViolations['financingType']}</FieldError>
        ) : null}
      </Col>
      {isLoanFinancing(context.financingType) ? (
        <Col className={FIELD_CARD}>
          <CardHeading title="Loan details" />
          <Radio.Group
            className={RADIO_GROUP_AS_FIELD}
            label={
              <Text as="span" className={FIELD_LABEL}>
                Lender
              </Text>
            }
            value={context.lenderType ?? ''}
            onBlur={onBlur('lender')}
            onValueChange={(value) => send({ type: 'SET_LENDER_TYPE', value: String(value) })}
            error={
              shouldShowError('lender') && context.lenderType == null
                ? 'Select the lender.'
                : undefined
            }
          >
            {LENDER_OPTIONS.map((option) => (
              <Radio.Item
                key={option.value}
                label={option.label}
                value={option.value}
                className={RADIO_ROW}
              />
            ))}
          </Radio.Group>
          {context.fieldViolations['lenderType'] != null ? (
            <FieldError>{context.fieldViolations['lenderType']}</FieldError>
          ) : null}
          {context.lenderType === 'non_partner' ? (
            <Row className="items-center gap-4 rounded-[12px] bg-od-bg-success/10 p-4">
              <IconCoin className="size-6 shrink-0 text-od-content-success" />
              <Text className="text-body-sm text-od-content-success">
                Opendoor Home Loans saves your buyer up to $21,600.
                <br />
                No origination points, no additional fees.{' '}
                <Link
                  href="/home-loans"
                  target="_blank"
                  rel="noopener noreferrer"
                  analyticsName="cosmos-make-offer-details-home-loans"
                  className="text-od-content-success"
                >
                  Learn more
                </Link>
              </Text>
            </Row>
          ) : null}
          <Col className="gap-2">
            <Text as="label" htmlFor="down-payment" className={FIELD_LABEL}>
              Down payment
            </Text>
            <Input
              id="down-payment"
              type="currency"
              decimals={0}
              min={0}
              value={context.downPaymentCents != null ? context.downPaymentCents / 100 : null}
              onValueChange={handleDownPaymentChange}
              onBlur={onBlur('down-payment')}
              placeholder="$"
              size="xs"
              className={FIELD_INPUT}
              aria-label="Down payment"
              aria-invalid={downPaymentInvalid}
              error={downPaymentInvalid}
              analyticsName="cosmos-make-offer-details-down-payment"
            />
            {context.fieldViolations['downPaymentCents'] != null ? (
              <FieldError>{context.fieldViolations['downPaymentCents']}</FieldError>
            ) : null}
            {!isMoneyAskValid(context.downPaymentCents, context.priceCents) ? (
              <FieldError>Down payment can’t exceed the offer amount.</FieldError>
            ) : null}
            {downPaymentMissing ? (
              <FieldError>Down payment cannot be empty for loan financing</FieldError>
            ) : null}
          </Col>
        </Col>
      ) : null}
      <Col className={FIELD_CARD}>
        <Text id="close-date-label" className={CARD_TITLE}>
          Closing date
        </Text>
        <DatePicker
          compactCalendar
          id="close-date"
          date={context.closeDate != null ? new Date(`${context.closeDate}T00:00:00`) : undefined}
          onSelect={(date) =>
            send({ type: 'SET_CLOSE_DATE', value: date.toISOString().slice(0, 10) })
          }
          onClear={() => send({ type: 'CLEAR_CLOSE_DATE' })}
          onBlur={() => markBlurred('close-date')}
          isOutOfRange={isDayOutOfRange}
          placeholder="MM/DD/YY"
          ariaLabelledBy="close-date-label"
          fullWidthTrailingIcon
          error={closeDateInvalid}
        />
        {closeDateViolation != null ? <FieldError>{closeDateViolation}</FieldError> : null}
        {closeDateError() ? <FieldError>{closeDateError()}</FieldError> : null}
        {context.financingType === 'fha' && !fhaEligible && fhaUntil !== '' ? (
          <Row className="items-start gap-3 rounded-[12px] bg-od-bg-accent-brand/10 p-4">
            <IconInfoCircle className="size-5 shrink-0 text-od-content-accent-brand" />
            <Text className="text-body-sm text-od-content-secondary">
              <Text as="span" className="font-medium text-od-content-primary">
                This home is FHA-eligible starting {fhaUntil}.
              </Text>{' '}
              FHA offers must close on or after this date.
            </Text>
          </Row>
        ) : null}
      </Col>
      <Col className={FIELD_CARD}>
        <CardHeading title="Contingency periods" />
        {periods.map(({ value, waived, maxDays, errors, invalid, dayParts, ...period }) => (
          <Col key={period.id} className="gap-2">
            <Row className="items-center justify-between gap-4">
              <Text as="label" htmlFor={period.id} className={FIELD_LABEL}>
                {period.label}
              </Text>
              <Row className="shrink-0 items-center">
                <Checkbox
                  size="xs"
                  value={period.id}
                  checked={waived}
                  onCheckedChange={(checked) =>
                    send({ type: period.event, value: checked ? 0 : period.defaultValue })
                  }
                  caption={
                    <Text as="span" className="text-body-sm text-od-content-secondary">
                      Waived
                      <Text as="span" className="sr-only">{` ${period.label}`}</Text>
                    </Text>
                  }
                  analyticsName={`cosmos-make-offer-details-${period.id}-waived`}
                />
              </Row>
            </Row>
            <Col className="relative w-full">
              <Input
                id={period.id}
                type="number"
                decimals={0}
                min={0}
                max={maxDays}
                value={value}
                onValueChange={(next) => send({ type: period.event, value: wholeDays(next) })}
                onBlur={onBlur(period.id)}
                disabled={waived}
                placeholder={period.placeholder}
                size="xs"
                className={FIELD_INPUT}
                aria-label={period.label}
                aria-describedby={`${period.id}-unit`}
                aria-invalid={invalid}
                error={invalid}
                analyticsName={`cosmos-make-offer-details-${period.id}`}
              />
              <Text as="span" id={`${period.id}-unit`} className="sr-only">
                days
              </Text>
              {/* Keep the unit outside the editable value so backspace only removes digits. */}
              {value != null ? (
                <Text
                  as="span"
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center whitespace-pre text-body-md"
                >
                  {dayParts.map((part, index) => (
                    <Text as="span" key={`${part.type}-${index}`} className={part.className}>
                      {part.value}
                    </Text>
                  ))}
                </Text>
              ) : null}
            </Col>
            {errors.map((error) => (
              <FieldError key={error}>{error}</FieldError>
            ))}
          </Col>
        ))}
      </Col>
      <Col className={FIELD_CARD}>
        <Radio.Group
          className={RADIO_GROUP_AS_CARD_TITLE}
          label={
            <Text as="span" className={CARD_TITLE}>
              Home sale contingency
            </Text>
          }
          value={contingentValue()}
          onBlur={onBlur('home-sale-contingency')}
          onValueChange={(value) =>
            send({ type: 'SET_CONTINGENT_ON_SALE', value: value === 'yes' })
          }
          error={
            shouldShowError('home-sale-contingency') && context.contingentOnSale == null
              ? 'Select yes or no.'
              : undefined
          }
        >
          <Radio.Item label="Yes" value="yes" className={RADIO_ROW} />
          <Radio.Item label="No" value="no" className={RADIO_ROW} />
        </Radio.Group>
        {context.contingentOnSale ? (
          <Radio.Group
            className={RADIO_GROUP_AS_CARD_TITLE}
            label={
              <Text as="span" className={CARD_TITLE}>
                Contingent home status
              </Text>
            }
            value={context.contingentHomeStatus ?? ''}
            onBlur={onBlur('contingent-home-status')}
            onValueChange={(value) =>
              send({ type: 'SET_CONTINGENT_HOME_STATUS', value: String(value) })
            }
            error={
              shouldShowError('contingent-home-status') && !statusOk
                ? 'Select the status of the contingent home.'
                : undefined
            }
          >
            {CONTINGENT_STATUS_OPTIONS.map((option) => (
              <Radio.Item
                key={option.value}
                label={option.label}
                value={option.value}
                className={RADIO_ROW}
              />
            ))}
          </Radio.Group>
        ) : null}
        {context.fieldViolations['buyerHomeSaleStatus'] != null ? (
          <FieldError>{context.fieldViolations['buyerHomeSaleStatus']}</FieldError>
        ) : null}
      </Col>
    </>
  );
};

export default DetailsStep;
